import { keepPreviousData, queryOptions, useMutation, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { qk } from "@/lib/query-keys";
import { toastError, toastExito } from "@/lib/toast";
import { extractApiMessage } from "@/shared/api/extract-message";
import {
  buscarHistorico,
  exportarHistoricoZip,
  obtenerFiltrosHistorico,
  type HistoricoParams,
} from "../services/historico.api";

/** Combos + defaults del periodo (RN-01/RN-04) · cambian poco, caché larga. */
export const filtrosHistoricoQueryOptions = () =>
  queryOptions({
    queryKey: qk.historicoFiltros,
    queryFn: obtenerFiltrosHistorico,
    staleTime: 5 * 60_000,
  });

/**
 * Búsqueda paginada · los filtros forman parte del queryKey (refetch al
 * cambiar) y `keepPreviousData` evita el salto al paginar (spec §7.1).
 */
export const busquedaHistoricoQueryOptions = (params: HistoricoParams) =>
  queryOptions({
    queryKey: qk.historicoBusqueda(params),
    queryFn: () => buscarHistorico(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });

export function useFiltrosHistorico() {
  return useQuery(filtrosHistoricoQueryOptions());
}

export function useBusquedaHistorico(params: HistoricoParams) {
  return useQuery(busquedaHistoricoQueryOptions(params));
}

/**
 * Como el request pide `responseType: "blob"`, un error llega con el
 * ProblemDetails ENVUELTO en un Blob · se lee su texto para recuperar el
 * detail real (ej. el tope HIST_ZIP_EXCEDE_TOPE con el máximo configurado).
 */
async function mensajeDeErrorBlob(error: unknown): Promise<string> {
  if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
    try {
      const problem = JSON.parse(await error.response.data.text()) as {
        detail?: string;
        title?: string;
      };
      return problem.detail ?? problem.title ?? error.message;
    } catch {
      return error.message;
    }
  }
  return extractApiMessage(error, "No se pudo exportar.");
}

/** Exportación ZIP por lote (RN-12) · descarga vía blob dentro del service. */
export function useExportarZip() {
  return useMutation({
    mutationFn: exportarHistoricoZip,
    onSuccess: () => {
      toastExito({ title: "Exportación lista", message: "El ZIP con los documentos seleccionados se descargó." });
    },
    onError: async (error: unknown) => {
      toastError({ title: "No se pudo exportar", message: await mensajeDeErrorBlob(error) });
    },
  });
}
